import type { Schemas } from "@neio/contracts";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { apiGet, switchedOff } from "../../lib/http";
import { FeatureOff } from "../FeatureOff";
import { Empty, ErrorState, Loading } from "../States";
import { utcTime } from "./format";
import { TableScroll } from "../TableScroll";

export type AuditEvent = Schemas["AuditEventView"];

export interface AuditFilters {
  package: string;
  /** A day, `YYYY-MM-DD`, from a date input. Empty for no lower bound. */
  since: string;
  action: string;
  limit: number;
}

export const NO_FILTERS: AuditFilters = { package: "", since: "", action: "", limit: 200 };
const LIMITS = [50, 200, 1000];

export interface AuditQuery { package?: string; since?: string; action?: string; limit: number }

/** The query the API takes: empty filters left out, and a day read as its UTC midnight. */
export function auditParams(filters: AuditFilters): AuditQuery {
  const pkg = filters.package.trim();
  const action = filters.action.trim();
  return {
    ...(pkg ? { package: pkg } : {}),
    ...(filters.since ? { since: `${filters.since}T00:00:00Z` } : {}),
    ...(action ? { action } : {}),
    limit: filters.limit,
  };
}

export function fetchAudit(filters: AuditFilters): Promise<AuditEvent[]> {
  return apiGet("/api/v1/audit", { query: auditParams(filters) });
}

/** Every action name the API records (`AUDIT_ACTIONS` in the audit module), for the filter. */
export function fetchAuditActions(): Promise<string[]> {
  return apiGet("/api/v1/audit/actions") as Promise<string[]>;
}

const ACTION_WORDS: Record<string, string> = {
  read: "page or file read",
  approve_documents: "documents approved",
  approve_spec: "vendor spec approved",
  case_resolution: "case resolution recorded",
  case_state: "case moved",
  confirm_documents: "documents confirmed for reading",
  index_notices: "notices indexed",
  index_visits: "visit reports indexed",
  ltb_approve: "last-time buy approved",
  ltb_close: "last-time buy closed",
  ltb_create: "last-time buy proposed",
  ltb_record_buy: "last-time buy recorded",
  ltb_resolve: "last-time buy follow-on recorded",
  open_case: "case opened",
  outreach_sent: "vendor enquiry sent",
  pid_import: "P&ID tags imported",
  pid_read_again: "P&ID sheet read again",
  pid_review: "P&ID reading reviewed",
  pid_upload: "P&ID uploaded",
  review_decision: "review decision",
  review_threshold_change: "review threshold changed",
  run_decision: "module run decision",
  threshold_change: "threshold changed",
  uc1_field_override: "extraction value chosen",
};

/** An action name in plain words. A name added to the API later reads with spaces for underscores. */
export function actionLabel(action: string): string {
  return ACTION_WORDS[action] ?? action.replace(/_/g, " ");
}

/** The filters a URL query names. Anything unreadable falls back to no filter. */
export function filtersFromSearch(params: URLSearchParams): AuditFilters {
  const limit = Number(params.get("limit"));
  const since = params.get("since") ?? "";
  return {
    package: params.get("package") ?? "",
    since: /^\d{4}-\d{2}-\d{2}$/.test(since) ? since : "",
    action: params.get("action") ?? "",
    limit: LIMITS.includes(limit) ? limit : NO_FILTERS.limit,
  };
}

/** The URL query for these filters, empty ones and the default limit left out. */
export function searchFromFilters(filters: AuditFilters): URLSearchParams {
  const out = new URLSearchParams();
  if (filters.package.trim()) out.set("package", filters.package.trim());
  if (filters.since) out.set("since", filters.since);
  if (filters.action.trim()) out.set("action", filters.action.trim());
  if (filters.limit !== NO_FILTERS.limit) out.set("limit", String(filters.limit));
  return out;
}

const MAX_DETAIL = 120;

function valueText(value: unknown): string {
  if (Array.isArray(value)) {
    const shown = value.slice(0, 3).map(valueText).join(", ");
    return value.length > 3 ? `${shown} and ${value.length - 3} more` : shown;
  }
  if (value === null || value === undefined) return "none";
  if (typeof value === "object") return `{${Object.keys(value).map((k) => k.replace(/_/g, " ")).join(", ")}}`;
  return String(value);
}

/** An event's detail as one short line, `key value, key value`. */
export function detailText(detail: AuditEvent["detail"]): string {
  if (!detail) return "";
  const text = Object.entries(detail).map(([k, v]) => `${k.replace(/_/g, " ")} ${valueText(v)}`).join(", ");
  return text.length > MAX_DETAIL ? `${text.slice(0, MAX_DETAIL)}...` : text;
}

function Detail({ detail }: { detail: AuditEvent["detail"] }) {
  const short = detailText(detail);
  if (!detail) return null;
  const nested = Object.values(detail).some((v) => v !== null && typeof v === "object");
  if (!nested && !short.endsWith("...")) return <>{short}</>;
  return (
    <details className="audit-detail">
      <summary>{short}</summary>
      <pre>{JSON.stringify(detail, null, 2)}</pre>
    </details>
  );
}

export function AuditTable({ rows }: { rows: AuditEvent[] }) {
  if (rows.length === 0) {
    return <Empty title="No audit events match these filters">Widen the time range or clear the action.</Empty>;
  }
  return (
    <div className="card table-scroll">
      <TableScroll label="Audit log">
      <table>
        <thead>
          <tr><th>When</th><th>Who</th><th>Action</th><th>Package</th><th>Target</th><th>Detail</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="num nowrap">{utcTime(r.at)}</td>
              <td>{r.actor}</td>
              <td>{actionLabel(r.action)}</td>
              <td>{r.package ?? <span className="muted">All packages</span>}</td>
              <td className="wrap-anywhere">{r.target ?? ""}</td>
              <td className="wrap-anywhere muted"><Detail detail={r.detail} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      </TableScroll>
    </div>
  );
}

export function AuditFilterForm({
  value, onApply, packages, actions,
}: {
  value: AuditFilters;
  onApply: (f: AuditFilters) => void;
  packages: string[] | null;
  actions: string[];
}) {
  const [draft, setDraft] = useState(value);
  const set = (patch: Partial<AuditFilters>) => setDraft((d) => ({ ...d, ...patch }));
  return (
    <form className="filters" onSubmit={(e) => { e.preventDefault(); onApply(draft); }} aria-label="Audit filters">
      <label className="field">
        <span>Package</span>
        {packages ? (
          <select value={draft.package} onChange={(e) => set({ package: e.target.value })}>
            <option value="">Every package I can read</option>
            {packages.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        ) : (
          <input value={draft.package} placeholder="Any" onChange={(e) => set({ package: e.target.value })} />
        )}
      </label>
      <label className="field">
        <span>Since</span>
        <input type="date" value={draft.since} onChange={(e) => set({ since: e.target.value })} />
      </label>
      <label className="field">
        <span>Action</span>
        <select value={draft.action} onChange={(e) => set({ action: e.target.value })}>
          <option value="">Any action</option>
          {/* A filter from an old link that the API no longer lists still shows as chosen. */}
          {[...new Set([...actions, ...(draft.action ? [draft.action] : [])])].map((a) => (
            <option key={a} value={a}>{actionLabel(a)}</option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Show</span>
        <select value={draft.limit} onChange={(e) => set({ limit: Number(e.target.value) })}>
          {LIMITS.map((n) => <option key={n} value={n}>{n} newest</option>)}
        </select>
      </label>
      <button type="submit"><Search size={14} aria-hidden="true" /> Apply</button>
      <button type="button" className="ghost" onClick={() => { setDraft(NO_FILTERS); onApply(NO_FILTERS); }}>Clear</button>
    </form>
  );
}

export function AuditTab({ packages }: { packages: string[] | null }) {
  // The filters are the URL's, so a filtered view can be linked, reloaded and gone back to.
  const [params, setParams] = useSearchParams();
  const filters = filtersFromSearch(params);
  const setFilters = (f: AuditFilters) => setParams(searchFromFilters(f));
  const query = useQuery({ queryKey: ["admin", "audit", filters], queryFn: () => fetchAudit(filters) });
  const known = useQuery({ queryKey: ["admin", "audit", "actions"], queryFn: fetchAuditActions });
  const actions = known.data ?? [...new Set((query.data ?? []).map((r) => r.action))].sort();
  const off = switchedOff(query.error, [404]);
  return (
    <div className="stack">
      <p className="muted">Every approval, decision, setting change and register read, newest first. Times are UTC.</p>
      <AuditFilterForm key={params.toString()} value={filters} onApply={setFilters} packages={packages} actions={actions} />
      {query.isPending ? <Loading what="Reading the audit log" />
        : off !== null ? <FeatureOff feature="The audit log" reason={off} />
          : query.error ? <ErrorState error={query.error} onRetry={() => query.refetch()} />
            : <AuditTable rows={query.data} />}
    </div>
  );
}
